import { resolveClientHomeLandingTab } from '@/lib/clients/client-home-landing';
import type { RacUtopiaFlags } from '@/lib/rac/rac-utopia-feature-flags';
import { isRacUtopiaFlagEnabled } from '@/lib/rac/rac-utopia-feature-flags';

export const CLIENT_RECORD_TAB_IDS = [
  'overview',
  'sah-hub',
  'rac-hub',
  'care-plans',
  'clinical',
  'assessments',
  'case-notes',
  'services',
  'medications',
  'devices',
  'incidents',
  'documents',
  'budget',
  'authorisations',
  'cm-time',
  'consent',
  'family',
  'history',
] as const;

export type ClientRecordTabId = (typeof CLIENT_RECORD_TAB_IDS)[number];

export function isClientRecordTabId(value: string | null | undefined): value is ClientRecordTabId {
  return typeof value === 'string' && (CLIENT_RECORD_TAB_IDS as readonly string[]).includes(value);
}

/** care_today_v1 ON-only work-area aliases for the renamed record areas. */
export const CARE_TODAY_WORK_AREA_TAB_ALIASES: Readonly<Record<string, ClientRecordTabId>> = {
  'care-today': 'overview',
  'daily-care': 'services',
  about: 'family',
};

/**
 * care_today_v1 ON: resolve a Care Today alias to its canonical tab id before
 * it reaches resolvers that only know the established ids. OFF is identity.
 */
export function normalizeCareTodayTabParam<T extends string | null | undefined>(
  tabParam: T,
  careToday: boolean,
): T | ClientRecordTabId {
  if (!careToday || !tabParam) return tabParam;
  return Object.hasOwn(CARE_TODAY_WORK_AREA_TAB_ALIASES, tabParam)
    ? (CARE_TODAY_WORK_AREA_TAB_ALIASES[tabParam] ?? tabParam)
    : tabParam;
}

/**
 * Phase 1: honour an explicit ?tab= deep link when present at mount / URL sync.
 */
export function parseRequestedClientTab(
  tabParam: string | null | undefined,
  options: { careToday?: boolean } = {},
): ClientRecordTabId | null {
  if (!tabParam) return null;
  if (isClientRecordTabId(tabParam)) return tabParam;

  // The client record's current work-area navigation uses these keys in links,
  // while the rendered content still uses the legacy tab ids above. Resolve
  // both forms through the same active-view and visibility checks.
  const workAreaTab: Partial<Record<string, ClientRecordTabId>> = {
    home: 'overview',
    ...(options.careToday ? CARE_TODAY_WORK_AREA_TAB_ALIASES : {}),
    summary: 'overview',
    plan: 'care-plans',
    delivery: 'services',
    funding: 'budget',
    people: 'family',
    records: 'documents',
    safety: 'incidents',
  };
  return workAreaTab[tabParam] ?? null;
}

export type SahHubDefaultPromotionInput = {
  flagEnabled: boolean;
  sahHubVisible: boolean;
  hasExplicitTabParam: boolean;
  sahHubTabRenderable: boolean;
};

/**
 * Phase 2: after client + module gates resolve, promote SAH clients to Journey
 * when the rollout flag is on and the user did not request a tab.
 */
export function shouldPromoteSahHubDefaultTab(input: SahHubDefaultPromotionInput): boolean {
  if (!input.flagEnabled) return false;
  if (input.hasExplicitTabParam) return false;
  if (!input.sahHubVisible) return false;
  return input.sahHubTabRenderable;
}

export type ResolveInitialClientTabInput = {
  requestedTab?: string | null | undefined;
  racHubVisible: boolean;
  sahHubVisible: boolean;
  racFlags: Pick<RacUtopiaFlags, 'RAC_JOURNEY_DEFAULT_TAB'>;
  mixedProgramHubDefault?: boolean;
};

/**
 * RAC default-tab resolution: honour explicit ?tab= first, then promote rac-hub
 * when the RAC default-tab flag is on and the client is not co-enrolled.
 */
export function resolveInitialClientTab(input: ResolveInitialClientTabInput): ClientRecordTabId {
  const requested = parseRequestedClientTab(input.requestedTab);
  if (requested) return requested;

  if (shouldPromoteRacHubDefaultTab(input)) {
    return 'rac-hub';
  }

  return 'overview';
}

export function shouldPromoteRacHubDefaultTab(input: ResolveInitialClientTabInput): boolean {
  if (!input.racHubVisible) return false;
  if (!isRacUtopiaFlagEnabled(input.racFlags, 'RAC_JOURNEY_DEFAULT_TAB')) return false;

  const coEnrolled = input.racHubVisible && input.sahHubVisible;
  if (coEnrolled && !input.mixedProgramHubDefault) return false;

  return true;
}

export type ComputeDefaultClientTabInput = {
  racHubVisible: boolean;
  sahHubVisible: boolean;
  /** Is the SAH-hub tab actually renderable for this client (passes module + chrome gates)? */
  sahHubTabRenderable: boolean;
  racFlags: Pick<RacUtopiaFlags, 'RAC_JOURNEY_DEFAULT_TAB'>;
  mixedProgramHubDefault?: boolean;
  /** care_today_v1 flag; OFF keeps the established landing. */
  careToday?: boolean;
};

/**
 * The tab a client record should show when the URL carries no explicit `?tab=`.
 *
 * Pure precedence (no side effects): SAH-hub default promotion wins first, then
 * RAC-hub default promotion, then plain `overview`. This collapses the former
 * two imperative default-promotion effects (SAH + RAC) into a single derivable
 * value so the active tab can be a `useMemo` with no `setState` round-trip — the
 * shape that makes the URL<->state ping-pong (#185 "Maximum update depth")
 * impossible by construction.
 */
export function computeDefaultClientTab(input: ComputeDefaultClientTabInput): ClientRecordTabId {
  // D1: one optimised Home — never default to Journey as a second home.
  return resolveClientHomeLandingTab({
    requestedTab: null,
    sahHubVisible: input.sahHubVisible,
    racHubVisible: input.racHubVisible,
    racJourneyDefaultEnabled: isRacUtopiaFlagEnabled(input.racFlags, 'RAC_JOURNEY_DEFAULT_TAB'),
    mixedProgram: Boolean(input.sahHubVisible && input.racHubVisible),
    careToday: input.careToday,
  });
}

export function clientTabSearchParamFor(tab: ClientRecordTabId): string | null {
  return tab === 'overview' ? null : tab;
}

export function buildClientTabUrl(
  pathname: string,
  tab: ClientRecordTabId,
  search: string,
  hash = '',
): string {
  const params = new URLSearchParams(search);
  const nextParam = clientTabSearchParamFor(tab);
  if (nextParam) {
    params.set('tab', nextParam);
  } else {
    params.delete('tab');
  }
  const query = params.toString();
  const normalizedHash = hash && hash.startsWith('#') ? hash : hash ? `#${hash}` : '';
  return `${query ? `${pathname}?${query}` : pathname}${normalizedHash}`;
}
